/*
  New Arrivals V2 — "wheel" carousel.

  Geometry model
  --------------
  Cards stand on the rim of a horizontal cylinder, like horses on a
  carousel. ONE number drives the whole layout: the cylinder's radius R.
  Everything else is derived from it, which is what makes the spacing
  identical between every pair of neighbours at every viewport size:

    a = k * step                    (k = signed distance from centre, may be fractional)
    x = R * sin(a)
    z = R * cos(a) - R              (so the centred card sits at z = 0)
    rotateY = a                     (keeps the card tangent to the rim)

  `step` is not authored — it is SOLVED from R so that the straight-line
  distance between two neighbouring rim positions equals one card width
  plus a deliberate gap:

    chord = 2R * sin(step / 2) = cardWidth * (1 + gapRatio)
    => step = 2 * asin((1 + gapRatio) * cardWidth / (2R))

  Because the chord is always wider than a card, cards can never overlap —
  the old implementation's fixed 0.65-card-width offset guaranteed that
  they would.

  Input model
  -----------
  A real, natively-scrollable strip (`[data-carousel-scroller]`) sits
  invisibly on top of the stage and is the single source of truth for
  position. Reading `scrollLeft / stepPx` gives a fractional card index,
  which feeds the geometry above every frame. Doing it this way means
  touch swipe, trackpad, click-drag, momentum and keyboard all come from
  the browser rather than from hand-written gesture code, and every input
  path lands in exactly the same place — no per-input drift or
  inconsistent card states.

  The strip deliberately does NOT use CSS scroll-snap: `scroll-snap-type:
  x mandatory` re-snaps whenever anything (including this file's own
  drag handling, and homepage-scroll-snap.js feeding it wheel deltas)
  writes to scrollLeft, which fights those writes. Snapping is done here
  instead, on scroll-idle, via a normal smooth scrollTo.

  Homepage integration
  --------------------
  None needed any more, which is the point. This section used to be tagged
  [data-snap-scroll-x] so that assets/homepage-scroll-snap.js would feed it
  vertical wheel input and hold the page in place until every product had
  been scrolled past. That file no longer pages the homepage at all — it
  just eases the rest position onto a section boundary once scrolling stops
  near one — and this section no longer claims vertical input. Scrolling
  down the homepage passes this section like any other. See the Wheel
  section below.
*/
(function () {
  var REDUCED_MOTION = window.matchMedia("(prefers-reduced-motion: reduce)");

  /* Hard ceiling on tilt, whatever the merchant picks.

     Critically this applies to the outermost card that is RENDERED AT ALL
     — the one at the far end of the fade tail — not to the outermost card
     inside the nominal "cards visible" band. Those tail cards are still
     partly opaque, and capping only the band let them swing on round to
     78°+ at low product counts, where they both overlapped their
     neighbours and hit the collapse angle described below. */
  var MAX_ANGLE_DEG = 62;

  /* Camera distance, as a multiple of the wheel's radius.

     This CANNOT be a fixed px value. A card sitting on the rim is tangent
     to the circle, so at one specific angle it lies exactly along the ray
     from the camera to itself and projects to zero width — a 1px vertical
     line. That angle is:

       cos(angle) = radius / (radius + cameraDistance)

     With a fixed 1500px camera and a radius that grows with card width,
     that collapse angle drifts straight into the range the outer cards
     actually occupy (at a 385px card it landed on 71°, which is where the
     outermost card sat). Tying the camera to the radius pins the collapse
     angle to a constant instead: at 3.6 it is acos(1/4.6) = 77.4°, a
     15-degree margin over MAX_ANGLE_DEG at every viewport size and product
     count.

     Lower values give the wheel more depth (nearer camera, stronger
     foreshortening between the centre card and its neighbours) at the cost
     of a narrower outermost card. Swept from 3.2 to 5: this is the lowest
     that keeps every visible card at least a quarter of its true width. */
  var PERSPECTIVE_RATIO = 3.6;

  /* Opacity floor for the outermost card. The section's whole job is to
     show the new arrivals, so no card is allowed to fade into a ghost the
     way the previous 0.22-per-step falloff did — it reached ~12% by the
     third card out, which is what made the sides of the section read as
     empty space. */
  var OPACITY_MIN = 0.62;
  var OPACITY_STEP = 0.13;

  /* Size falloff away from the centre, on top of the wheel's own
     perspective. The perspective alone barely separates the centred card
     from its neighbours, so the focused product didn't read as the focus.
     Scale is linear in distance (so it eases smoothly mid-turn) with a
     floor, so the outer cards stay clearly legible products rather than
     thumbnails: 1 at centre, 0.84 next to it, 0.74 beyond. Tunable per
     section via --side-card-scale-step / --side-card-scale-min. */
  var SCALE_STEP = 0.16;
  var SCALE_MIN = 0.74;

  /* Longest distance (in card slots) over which a card is allowed to fade
     out as it passes the visible-count limit. Fractional, so the fade
     happens smoothly mid-scroll rather than as a pop. The actual value is
     solved per-section in measure(), because it must also be short enough
     to finish before the wrap boundary — see there. */
  var EDGE_FADE_MAX = 0.7;

  /* Quiet time before the strip snaps to the nearest card. Wheel and mouse
     drag both end decisively, so they can be tidied up almost immediately;
     touch cannot (see idleDelay() and the Touch section in init()). */
  var SCROLL_IDLE_MS = 110;
  var TOUCH_IDLE_MS = 260;

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function init(root) {
    if (!root || root.dataset.newArrivalsV2Init) return;
    root.dataset.newArrivalsV2Init = "true";

    var stage = root.querySelector("[data-carousel-stage]");
    var scroller = root.querySelector("[data-carousel-scroller]");
    var rail = root.querySelector("[data-carousel-rail]");
    var prevBtn = root.querySelector("[data-carousel-prev]");
    var nextBtn = root.querySelector("[data-carousel-next]");
    if (!stage || !scroller || !rail) return;

    var slides = Array.prototype.slice.call(
      root.querySelectorAll("[data-carousel-card]")
    );
    var stops = Array.prototype.slice.call(
      rail.querySelectorAll("[data-carousel-stop]")
    );
    var dots = Array.prototype.slice.call(
      root.querySelectorAll("[data-carousel-dot]")
    );
    if (slides.length === 0 || stops.length !== slides.length) return;

    var count = slides.length;

    /* ---------- Infinite travel ----------

       render() already draws this section as a true ring: a card position
       is a function of (i - pos) taken modulo `count`, the short way
       round, so card 0 is drawn immediately to the right of the last card
       at all times. The wheel was never the bounded part. TRAVEL along it
       was: the rail carried exactly one stop per product, so scrollLeft
       ran out at the last one and the only way back to the first was a
       long reverse sweep back across everything.

       The fix is to give the rail several identical copies of the product
       set and keep the visitor in the middle one. Because the rendered
       frame depends only on `pos` modulo `count`, adding or subtracting a
       whole cycle (count * stepPx) leaves every card transform, opacity
       and z-index bit-for-bit identical -- so the correction is invisible.
       Travel is then unbounded in both directions: forward stays forward,
       and the wheel simply keeps turning the same way.

       recenter() only ever fires when the strip is AT REST (see its
       guards). Writing scrollLeft mid-gesture kills iOS momentum dead,
       which is the usual reason hand-rolled infinite carousels feel worse
       than bounded ones. Resting-only correction costs nothing here,
       because CYCLES leaves three full cycles of runway on either side --
       far more than one flick covers before the strip settles. */
    var LOOP = count >= 3;
    var CYCLES = LOOP ? 7 : 1;
    var HOME = (CYCLES - 1) / 2; // the cycle we keep the visitor in
    var total = count * CYCLES;
    var homeBase = HOME * count;

    /* The extra stops are pure scroll extent -- empty spans, no cards, no
       images. Cloning them here rather than emitting them from Liquid
       keeps the markup one-stop-per-product, so the stops.length !==
       slides.length guard above still means something. */
    if (LOOP) {
      var pad = document.createDocumentFragment();
      for (var c = 1; c < CYCLES; c++) {
        for (var j = 0; j < count; j++) {
          pad.appendChild(stops[j].cloneNode(false));
        }
      }
      rail.appendChild(pad);
      stops = Array.prototype.slice.call(
        rail.querySelectorAll("[data-carousel-stop]")
      );
    }

    /* Which copy of card `i` is nearest the visitor right now. Dots,
       keyboard focus and click-to-centre all name a product by its
       0..count-1 index; this resolves that to the copy the card is
       actually being DRAWN at, so the wheel turns the short way round --
       the direction the visitor saw that card in -- instead of unwinding
       across the rail. */
    function nearestIndexFor(i) {
      if (!LOOP) return i;
      var base = Math.round(progress());
      var delta = ((((i - base) % count) + count) % count);
      if (delta > count / 2) delta -= count;
      return base + delta;
    }

    /* Live geometry, recomputed on every resize. Kept in one object so a
       stale partial update can't leave, say, a new card width paired with
       the old solved step. */
    var geo = {
      cardWidth: 0,
      radius: 0,
      perspective: 1400,
      step: 0, // radians between neighbouring rim slots
      stepPx: 0, // scroller pixels that equal one card slot
      half: 1, // how many cards are shown either side of centre
      fade: 0.5 // slots over which a card fades out past that limit
    };

    var activeIndex = 0;
    var idleTimer = null;
    var rafPending = false;

    /* Touch gesture state. Mouse drags are tracked by `dragging` further
       down, but that handler ignores touch on purpose (the browser is
       already scrolling the strip natively for those), which left touch
       gestures with no guard at all against the idle-settle below. See the
       Touch section for what that broke. */
    var touching = false;
    var lastTouchAt = 0;

    /* Tap-target state for the price row (price + Add to cart).

       That row is a control, not a place to start turning the wheel, and
       on a touch screen the two were indistinguishable: a tap on Add to
       cart is also a touchstart on the scroller, so it cancelled the
       in-flight settle, stranded the wheel part-way between two cards and
       queued another settle 260ms later. The button then moved out from
       under the finger between press and release — by as much as 80px,
       measured, since a card mid-turn is both offset and rescaled — and
       the tap landed on nothing. A 34px target cannot survive that.

       Desktop never showed it because the wheel pauses under the cursor
       (see hoveringProduct), so by the time a mouse reaches the button
       everything has already stopped. A finger has no hover to announce
       itself with, and the first thing the section hears is the tap
       itself.

       So a touch that lands in that row is not a wheel gesture: it
       cancels no animation, strands the wheel nowhere, and queues no
       settle. It does not hold the wheel either — autoplay runs straight
       through the press, untouched, because the button takes the press
       through a captured pointer and no longer cares whether the card
       under it is moving. Nothing about the row reaches the carousel.

       `tapHold` is only ever set on coarse pointers — this is entirely a
       touch problem, and mouse/pen behaviour is unchanged. */
    var COARSE_POINTER = window.matchMedia("(hover: none), (pointer: coarse)");
    /* How far a finger may wander before the press stops being a tap and
       becomes a swipe. Comfortably under the browser's own scroll slop, so
       a gesture the browser has already committed to scrolling is one this
       file has long since handed over. */
    var TAP_SLOP = 10;
    var tapHold = false;
    var tapStartX = 0;
    var tapStartY = 0;

    // ---------- Geometry ----------

    function readNumber(name, fallback) {
      var raw = getComputedStyle(root).getPropertyValue(name);
      var value = parseFloat(raw);
      return isNaN(value) ? fallback : value;
    }

    /*
      Resolve the card's size against BOTH of the ceilings declared in CSS
      and write the winner to --card-h, which is what the card actually
      sizes itself from.

      This can't be done in CSS alone: a percentage in a `height` property
      resolves against the parent's height, so there is no way to say "no
      taller than 86% of the stage, and also no wider than a third of it"
      in one declaration. Getting it wrong is not cosmetic — sizing from
      height alone let the card grow wider than the viewport in any tall,
      narrow frame, which is the shape a phone gets.
    */
    function sizeCards() {
      /* Drop the previous pass's cap BEFORE measuring. --stage-max-h is
         this function's own output, applied as a max-height on the
         carousel wrap that the stage fills — so measuring with it still in
         place reads back the last answer instead of the space the section
         actually has. That only bites in one direction: a cap can make the
         stage smaller but never bigger, so shrinking (the mobile
         breakpoints, where the width ceiling wins) worked and growing back
         did not. Cards stayed phone-sized inside a full-height section
         until a reload cleared the inline properties. Reading clientHeight
         below flushes layout, so the measurement lands on the uncapped
         box. */
      root.style.removeProperty("--stage-max-h");

      var stageW = scroller.clientWidth;
      var stageH = stage.clientHeight;
      if (!stageW || !stageH) return;

      var heightRatio = readNumber("--card-height-ratio", 0.86);
      var maxWidthRatio = readNumber("--card-max-width-ratio", 0.3);
      var aspect = readNumber("--card-aspect-ratio", 0.6);
      if (aspect <= 0) aspect = 0.6;

      var byHeight = stageH * heightRatio;
      var byWidth = (stageW * maxWidthRatio) / aspect;
      var next = Math.round(Math.min(byHeight, byWidth));

      /* When the width ceiling is the one that wins — a tall, narrow frame,
         e.g. a tablet held portrait, which still gets the homepage's
         full-screen section — the stage is left much taller than the wheel
         needs and the section goes back to looking half empty. Capping the
         stage to the height the cards actually occupy lets the flex column
         collapse that dead space and centre the whole group instead.

         The cap is re-applied on every pass rather than only when the size
         changed, because it was cleared at the top: skipping it would
         leave the stage uncapped and the dead space back. It is set ONLY
         when the width ceiling won — when height is the binding constraint
         the wheel already fills the frame, and capping there would pin the
         stage to its current height for nothing. The measurement no longer
         sees the cap, so byHeight can't drift with it and the old 1px
         nudging is gone; the guard below now only avoids redundant writes
         of an unchanged --card-h. */
      var current = parseFloat(root.style.getPropertyValue("--card-h")) || 0;
      if (Math.abs(next - current) > 1) {
        root.style.setProperty("--card-h", next + "px");
      }
      if (byWidth < byHeight) {
        root.style.setProperty("--stage-max-h", Math.round(next / heightRatio) + "px");
      }
    }

    function measure() {
      sizeCards();

      /* offsetWidth, not getBoundingClientRect().width: the latter returns
         the card's PROJECTED width, which shrinks with rotation and depth.
         Measuring that would feed the wheel's own output back into its
         input — a resize while any card but the first was centred would
         read a foreshortened width and resolve a smaller wheel each time.
         offsetWidth is the untransformed layout width. */
      var sample = slides[0];
      geo.cardWidth = sample ? sample.offsetWidth : 0;
      if (!geo.cardWidth) return false;

      var gapRatio = readNumber("--card-gap-ratio", 0.14);
      var maxAngle = clamp(readNumber("--wheel-max-angle", 52), 20, MAX_ANGLE_DEG);
      var outerCap = (MAX_ANGLE_DEG * Math.PI) / 180;
      var visible = Math.max(3, readNumber("--cards-visible", 5));

      /* Two ceilings on how many cards flank the centre one. The merchant
         picks the first; the second is structural — cards wrap around the
         far side of the wheel (see render()), so the fan must finish
         fading out before it reaches the halfway point, or a card would
         visibly pop from one edge of the wheel to the other. */
      geo.half = Math.min((visible - 1) / 2, (count - 1) / 2);
      geo.fade = Math.min(EDGE_FADE_MAX, count / 2 - geo.half);

      /* The fan is authored from the OUTERMOST card's tilt, not from the
         radius, because that angle is the thing with a real constraint on
         it (readability, and MAX_ANGLE_DEG). The radius is then whatever
         value spaces the cards at that angle without overlapping: the
         straight-line distance between two neighbouring rim slots is held
         at one card width plus the gap.

           chord = 2 * radius * sin(step / 2) = cardWidth * (1 + gap)

         Because the chord is always wider than a card, cards can never
         overlap — which is precisely what the previous implementation's
         fixed 0.65-card-width offset could not promise. */
      /* Two ceilings on the angle between neighbouring slots: the
         merchant's, which sets the tilt of the last card in the visible
         band, and the structural one, which caps the last card of the
         fade tail. Whichever is tighter wins. At a low product count the
         tail is a big fraction of the fan, so the second is usually the
         one doing the work. */
      geo.step = Math.min(
        ((maxAngle * Math.PI) / 180) / Math.max(geo.half, 0.5),
        outerCap / (geo.half + geo.fade)
      );

      var chord = (1 + gapRatio) * geo.cardWidth;
      geo.radius = chord / (2 * Math.sin(geo.step / 2));
      geo.perspective = geo.radius * PERSPECTIVE_RATIO;

      geo.scaleStep = clamp(readNumber("--side-card-scale-step", SCALE_STEP), 0, 0.5);
      geo.scaleMin = clamp(readNumber("--side-card-scale-min", SCALE_MIN), 0.4, 1);

      /* One card slot costs slightly less than a card width of scrolling,
         so a drag feels like it's moving the wheel a touch more than 1:1
         — the standard carousel feel. */
      geo.stepPx = Math.max(80, Math.round(geo.cardWidth * 0.82));

      return true;
    }

    /* The rail is padded so slot 0 and slot count-1 can each come to rest
       dead centre of the scroller. Done in px from JS rather than as a
       percentage, because a percentage padding on a flex row resolves
       against the row's own content width, not the scroller's. */
    function layoutRail() {
      var sidePad = Math.max(0, (scroller.clientWidth - geo.stepPx) / 2);
      rail.style.paddingLeft = sidePad + "px";
      rail.style.paddingRight = sidePad + "px";
      stops.forEach(function (stop) {
        stop.style.width = geo.stepPx + "px";
      });

      /* The stage shares a grid cell with the rail, so that cell is as
         wide as the rail's full scrollable length — a CSS `width: 100%`
         on the stage would resolve against that, not against the part of
         the strip you can actually see. Setting it in px from here is
         what keeps the sticky stage exactly one screenful wide. */
      stage.style.width = scroller.clientWidth + "px";
      stage.style.perspective = Math.round(geo.perspective) + "px";

      /* Arrows hug the fan instead of sitting at the screen edges. Project
         the outer edge of the outermost resting card (slot geo.half) the
         same way the browser does — rotated about its own centre, then
         scaled by perspective about the stage centre — and hand CSS that
         half-width. Every rest position fills the same slots, so this
         doesn't change as the wheel turns. */
      var a = geo.half * geo.step;
      var halfW = (geo.cardWidth * scaleAt(geo.half)) / 2;
      var edgeX =
        geo.radius * Math.sin(a) -
        geo.cardWidth * shrinkAt(geo.half) * Math.cos(a) +
        halfW * Math.cos(a);
      var edgeZ = geo.radius * Math.cos(a) - geo.radius - halfW * Math.sin(a);
      var reach = (edgeX * geo.perspective) / (geo.perspective - edgeZ);
      root.style.setProperty("--fan-half-width", Math.round(reach) + "px");
    }

    // ---------- Render ----------

    /* Card scale at a (non-negative, fractional) distance from centre. */
    function scaleAt(absK) {
      return Math.max(geo.scaleMin, 1 - geo.scaleStep * absK);
    }

    /* How far (in card widths) a card at absK is pulled towards the centre
       to close the gap its shrinking — and every smaller card between it and
       the centre — would otherwise open up. It is the integral of (1 - scale)
       from 0 to absK: between neighbours at k and k+1 that integral is close
       to the sum of the two half-widths lost, so the visible gap between any
       pair of cards stays at the authored --card-gap-ratio. Being an
       integral, it is continuous, so nothing jumps mid-turn. */
    function shrinkAt(absK) {
      var d = geo.scaleStep;
      if (!d) return 0;
      var t0 = (1 - geo.scaleMin) / d;
      if (absK <= t0) return (d * absK * absK) / 2;
      return (d * t0 * t0) / 2 + (1 - geo.scaleMin) * (absK - t0);
    }

    function progress() {
      if (!geo.stepPx) return 0;
      return scroller.scrollLeft / geo.stepPx;
    }

    function render() {
      var pos = progress();

      slides.forEach(function (slide, i) {
        /* Signed, fractional distance from the front of the wheel, taken
           the short way round.

           The wrap is what keeps the section full at both ends. Scrolling
           itself is bounded — there is a real first and last product, so
           the wheel runs out and hands vertical scrolling back to the
           page — but if cards were only ever drawn in a straight line,
           resting on the first product would leave the entire left half
           of the section empty, which is the bare-looking whitespace this
           rebuild exists to remove. Drawing the last products round on
           the left instead is also just what a wheel does: it is
           continuous even though your travel along it is not.

           Nothing pops when a card crosses to the other side, because
           geo.fade is solved in measure() so a card has already reached
           zero opacity by the time it gets there. */
        var k = ((((i - pos) % count) + count) % count);
        if (k > count / 2) k -= count;
        /* A card at rest must be EXACTLY flat. On screens with a fractional
           pixel ratio (a Galaxy A52 is 2.625) the browser rounds scrollLeft
           to a physical pixel, so the resting position is a fraction of a
           CSS pixel off and k comes out as ±0.001 instead of 0. That is a
           rotateY of a few hundredths of a degree — invisible, but it tips
           one half of the centred card behind the track's own plane, and
           every tap on that half (half the colour band, half the size
           rings) was hit-tested to the track instead of the card. Phones
           with a whole-number pixel ratio land on 0 and never showed it.
           0.004 of a step is about a tenth of a degree. */
        if (k > -0.004 && k < 0.004) k = 0;
        var absK = Math.abs(k);

        var angle = k * geo.step;
        var scale = scaleAt(absK);
        var x =
          geo.radius * Math.sin(angle) -
          (k < 0 ? -1 : 1) * geo.cardWidth * shrinkAt(absK) * Math.cos(angle);
        var z = geo.radius * Math.cos(angle) - geo.radius;

        /* Cards past the visible limit fade out over geo.fade slots
           rather than being cut off at a hard index, so nothing pops in
           or out while the wheel is mid-turn. */
        var edge = clamp((geo.half + geo.fade - absK) / geo.fade, 0, 1);
        var opacity =
          Math.max(OPACITY_MIN, 1 - absK * OPACITY_STEP) * edge;

        /* translate(-50%, -50%) is applied first so every later function
           operates about the card's own centre; the card itself is pinned
           at top/left 50% in CSS. Pure translations commute, so this
           resolves to "rotate about my own centre, then move to my rim
           position" regardless of where it sits in the string. */
        slide.style.transform =
          "translate(-50%, -50%) translate3d(" +
          x.toFixed(2) +
          "px, 0, " +
          z.toFixed(2) +
          "px) rotateY(" +
          ((angle * 180) / Math.PI).toFixed(2) +
          "deg) scale(" +
          scale.toFixed(4) +
          ")";
        slide.style.opacity = opacity.toFixed(3);
        slide.style.zIndex = String(Math.round(1000 - absK * 100));

        /* Only the card nearest the centre may be interacted with. Side
           cards are inert so a click anywhere on them falls through to
           the scroller-level handler that centres them instead — one
           predictable rule for every off-centre card. */
        var isActive = absK < 0.5;
        slide.classList.toggle("wt-new-arrivals-v2__card-wrap--active", isActive);
        // "auto", not "": the track is pointer-events: none in the CSS (see
        // the note there), so the centred card has to opt back in itself.
        slide.style.pointerEvents = isActive && edge > 0 ? "auto" : "none";
        slide.setAttribute("aria-hidden", edge > 0 ? "false" : "true");

        /* Only the centred card is reachable by keyboard. Without this,
           tabbing through the section would stop on products that are
           rotated away and largely off-screen. The image link is already
           tabindex -1 / aria-hidden in the markup (it duplicates the title
           link), so in practice this governs the title link. */
        slide.querySelectorAll("[data-card-link]").forEach(function (link) {
          if (link.getAttribute("aria-hidden") === "true") return;
          link.tabIndex = isActive ? 0 : -1;
        });
        var add = slide.querySelector("[data-card-add]");
        if (add) add.tabIndex = isActive ? 0 : -1;
        slide.querySelectorAll("[data-card-swatch]").forEach(function (swatch) {
          swatch.tabIndex = isActive ? 0 : -1;
        });
      });

      var nearest = clamp(Math.round(pos), 0, total - 1);
      if (nearest !== activeIndex) {
        activeIndex = nearest;
        // activeIndex counts along the extended rail; the dots count
        // products, so fold it back down to 0..count-1.
        var dotIndex = ((activeIndex % count) + count) % count;
        dots.forEach(function (dot, i) {
          var on = i === dotIndex;
          dot.classList.toggle("wt-new-arrivals-v2__dot--active", on);
          dot.setAttribute("aria-selected", on ? "true" : "false");
        });
        // A new product is centred, however it got there: it gets a whole
        // interval, and its dot starts filling from empty.
        // A swatch press still in flight was the start of this turn, not a
        // choice: put that card back first (see pressSwatch).
        vlog("  wheel: card " + dotIndex + " centred");
        if (pressUndo) undoPress();
        // It also ends a swatch hold: that belonged to the card that left.
        swatchHold = false;
        resetAutoplayClock();
      }

      /* Travel is unbounded while looping, so neither arrow can ever run
         out of somewhere to go. Below three products there is no ring worth
         turning and the old bounded behaviour stands. */
      if (prevBtn) prevBtn.disabled = !LOOP && pos <= 0.02;
      if (nextBtn) nextBtn.disabled = !LOOP && pos >= count - 1 - 0.02;
    }

    function scheduleRender() {
      if (rafPending) return;
      rafPending = true;
      requestAnimationFrame(function () {
        rafPending = false;
        render();
      });
    }

    // ---------- Navigation ----------

    /*
      Every programmatic move (arrows, dots, click-to-centre, snapping
      after a gesture) is animated here rather than handed to
      `scrollTo({behavior: "smooth"})`. Three reasons, in order of
      importance:

      1. Native smooth scrolling is not dependable. It is disabled outright
         in several environments, where scrollTo() with that option becomes
         a silent no-op — the arrows and dots would simply do nothing.
      2. It makes the in-flight guard exact. A native animation emits scroll
         events the whole way, so the idle-settle timer below fires part-way
         through one, reads a position still nearer where the wheel STARTED
         than where it is heading, and "settles" it straight back to where
         it began. Owning the animation means knowing precisely when one is
         running.
      3. It matches the rest of the theme. easeOutExpo is the same curve
         smooth-scroll.js's Lenis instance and homepage-scroll-snap.js
         already use, so turning the wheel feels like the same site as
         moving between sections.
    */
    var ANIM_MS = 420;
    var animFrame = null;
    /* The last value the animation itself wrote. If a scroll event reports
       anything else while an animation is running, something outside this
       file moved the strip — on the homepage that is
       homepage-scroll-snap.js feeding it a wheel notch — and the user's
       input has to win. Without this, wheeling again during the ~420ms
       settle would be silently overwritten on the next frame and the
       section would feel unresponsive mid-gesture. */
    var lastWritten = -1;

    /* Two curves, chosen by who asked for the movement.

       easeOutExpo starts at full speed. That is exactly right when the
       visitor just clicked an arrow, a dot or an off-centre card: the strip
       leaps the instant they act, which is what makes a control feel
       responsive.

       A settle is the opposite situation. Nobody asked for it, the strip is
       already at rest, and it happens a beat after the gesture ended. A
       curve that opens at full speed reads there as a jerk — the section
       snatching itself into place — which is most of what makes an
       otherwise-native touch scroll feel snappy. Opening slowly instead
       makes the same correction read as the wheel easing itself straight. */
    function easeOutExpo(t) {
      return Math.min(1, 1.001 - Math.pow(2, -10 * t));
    }

    function easeInOutCubic(t) {
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }

    function cancelAnim() {
      if (animFrame !== null) {
        cancelAnimationFrame(animFrame);
        animFrame = null;
      }
      lastWritten = -1;
    }

    function maxScroll() {
      return Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    }

    /* Slide the visitor back to the middle cycle by a whole number of
       cycles. Visually a no-op (see the Infinite travel note above), so
       the only thing that matters is WHEN it runs: never mid-gesture and
       never mid-animation, or it would fight momentum and in-flight
       tweens. At rest scrollLeft sits on an exact multiple of stepPx, and
       shifting by whole cycles keeps it there. */
    function recenter() {
      if (!LOOP || !geo.stepPx) return;
      if (dragging || touching || animFrame !== null) return;

      var drift = Math.round((progress() - homeBase) / count);
      if (drift === 0) return;

      var shifted = scroller.scrollLeft - drift * count * geo.stepPx;
      // Never step outside the rail we actually built.
      if (shifted < 0 || shifted > maxScroll()) return;
      scroller.scrollLeft = shifted;
      activeIndex = Math.round(progress());
    }

    function scrollToIndex(index, smooth, easing) {
      window.clearTimeout(idleTimer);
      cancelAnim();

      var ease = easing || easeOutExpo;
      var target = clamp(clamp(index, 0, total - 1) * geo.stepPx, 0, maxScroll());
      var from = scroller.scrollLeft;

      if (!smooth || REDUCED_MOTION.matches || Math.abs(target - from) < 1) {
        scroller.scrollLeft = target;
        render();
        return;
      }

      var start = null;
      animFrame = requestAnimationFrame(function step(now) {
        if (start === null) start = now;
        var t = Math.min(1, (now - start) / ANIM_MS);
        scroller.scrollLeft = from + (target - from) * ease(t);
        lastWritten = scroller.scrollLeft;
        if (t < 1) {
          animFrame = requestAnimationFrame(step);
        } else {
          animFrame = null;
          // At rest here, which is the only safe moment to fold travel
          // back into the middle cycle.
          recenter();
        }
      });
    }

    function settle() {
      recenter();
      scrollToIndex(Math.round(progress()), true, easeInOutCubic);
    }

    /* How long the strip must be quiet before it snaps to the nearest card.

       Touch needs a longer window than wheel or drag, because a touch
       gesture has a phase nothing else has: the hand-off between the finger
       leaving the glass and the browser's momentum taking over. Scroll
       events stop for a moment in that gap, and anything short enough to
       fire inside it cuts the flick dead. TOUCH_IDLE_MS also has to outlast
       the ragged, widely-spaced scroll events at the tail of a decelerating
       momentum scroll, which is where the gaps are longest. */
    function idleDelay() {
      // Momentum from a touch can still be running well after touchend, so
      // this stays in touch mode for a moment rather than keying off
      // `touching` alone.
      return Date.now() - lastTouchAt < 1200 ? TOUCH_IDLE_MS : SCROLL_IDLE_MS;
    }

    scroller.addEventListener(
      "scroll",
      function () {
        scheduleRender();
        // `dragging` covers mouse/pen only — the pointerdown handler below
        // deliberately ignores touch — so `touching` is what stops a settle
        // from firing in the middle of a finger gesture.
        if (dragging || touching) return;

        if (animFrame !== null) {
          // Something outside this file moved the strip mid-animation (see
          // lastWritten) — yield to it rather than fighting it.
          if (Math.abs(scroller.scrollLeft - lastWritten) > 2) cancelAnim();
          // Otherwise the animation is still on its way; don't settle over it.
          else return;
        }
        window.clearTimeout(idleTimer);
        idleTimer = window.setTimeout(settle, idleDelay());
      },
      { passive: true }
    );

    // ---------- Touch ----------
    /*
      Touch scrolling of this strip is entirely the browser's — that is the
      whole input model, and it is why the pointer-drag handler below skips
      touch and why the section carries data-lenis-prevent-touch. What was
      missing was any guard stopping this file's own idle-settle from firing
      part-way through a native touch gesture.

      Without it, `dragging` was false for the entire gesture (it is only
      ever set for mouse and pen), so the 110ms idle timer ran throughout.
      Two things went wrong, and both read exactly as "snappy, not smooth":

      1. Pausing mid-drag — finger still down, deciding which product to
         look at — went quiet for longer than the timer, so the strip
         animated itself to the nearest card underneath a stationary finger,
         and continuing the drag then fought a 420ms animation.
      2. Releasing after a flick left a brief gap before the browser's
         momentum produced its first scroll event. The timer fired inside
         that gap and snapped, killing the flick before momentum ever
         started — a hard stop where there should have been a glide.

      Suppressing the settle while a finger is down, and giving the release
      a longer window, lets the browser's own momentum and deceleration play
      out in full. The snap then only tidies up the final resting position,
      which is all it was ever meant to do.
    */
    /* Is this touch a press on a card's controls rather than a grab of the
       wheel? Only ever true on a coarse pointer — see the tap-target note
       at the top of init(). */
    function inTapZone(e) {
      if (!COARSE_POINTER.matches) return false;
      var target = e.target;
      return !!(target && target.closest && target.closest("[data-card-actions]"));
    }

    /* The wheel-gesture path, split out of the touchstart handler so a tap
       that turns into a swipe can enter it late (see the touchmove handler
       below) on exactly the same terms as one that started as a swipe. */
    function beginTouchGesture() {
      touching = true;
      lastTouchAt = Date.now();
      window.clearTimeout(idleTimer);
      // A settle already in flight must yield instantly to a new touch,
      // or the first moments of the gesture fight a running animation.
      cancelAnim();
    }

    scroller.addEventListener(
      "touchstart",
      function (e) {
        /* A press on the price row starts as a tap: the wheel is left
           entirely alone — nothing cancelled, nothing scheduled — so the
           button stays exactly where the visitor aimed. Whether it really
           was a tap is decided by what the finger does next, not here,
           which is what keeps the row from becoming a dead spot that a
           swipe cannot start from. */
        if (inTapZone(e)) {
          tapHold = true;
          var touch = e.touches && e.touches[0];
          tapStartX = touch ? touch.clientX : 0;
          tapStartY = touch ? touch.clientY : 0;
          return;
        }
        beginTouchGesture();
      },
      { passive: true }
    );

    /* A press that wanders past TAP_SLOP was a swipe all along. Hand it to
       the wheel mid-gesture — the browser is already scrolling the strip
       natively by this point, so all that is needed is for this file to
       start guarding the settle as it would for any other touch. */
    scroller.addEventListener(
      "touchmove",
      function (e) {
        if (!tapHold) return;
        var touch = e.touches && e.touches[0];
        if (!touch) return;
        if (
          Math.abs(touch.clientX - tapStartX) < TAP_SLOP &&
          Math.abs(touch.clientY - tapStartY) < TAP_SLOP
        ) {
          return;
        }
        tapHold = false;
        beginTouchGesture();
      },
      { passive: true }
    );

    function endTouch() {
      /* A tap that stayed a tap: the wheel never moved for it, so there is
         nothing to settle. Releasing the hold restarts the autoplay clock
         from zero, giving the card a full interval before it turns again. */
      if (tapHold) {
        tapHold = false;
        lastTouchAt = Date.now();
        return;
      }
      if (!touching) return;
      touching = false;
      lastTouchAt = Date.now();
      /* Deliberately does NOT settle here, unlike endDrag() for the mouse.
         A released finger usually leaves momentum behind, and settling now
         would cancel it. Momentum emits its own scroll events, each one
         rescheduling the idle timer, so the settle lands naturally once the
         strip has actually come to rest. This timer only matters when the
         release produced no momentum at all — a slow drag let go without a
         flick, where no further scroll event is coming. */
      window.clearTimeout(idleTimer);
      idleTimer = window.setTimeout(settle, TOUCH_IDLE_MS);
    }

    scroller.addEventListener("touchend", endTouch, { passive: true });
    scroller.addEventListener("touchcancel", endTouch, { passive: true });

    if (prevBtn) {
      prevBtn.addEventListener("click", function () {
        scrollToIndex(Math.round(progress()) - 1, true);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener("click", function () {
        scrollToIndex(Math.round(progress()) + 1, true);
      });
    }
    dots.forEach(function (dot) {
      dot.addEventListener("click", function () {
        scrollToIndex(
          nearestIndexFor(parseInt(dot.getAttribute("data-index"), 10) || 0),
          true
        );
      });
    });

    /* Tabbing into a card that is currently off-centre must bring it to
       the centre, otherwise keyboard users can focus something they can't
       see. Cards themselves are real links, so focus order is native. */
    root.addEventListener("focusin", function (e) {
      var slide = e.target.closest && e.target.closest("[data-carousel-card]");
      if (!slide) return;
      var index = nearestIndexFor(
        parseInt(slide.getAttribute("data-index"), 10) || 0
      );
      if (Math.round(progress()) !== index) scrollToIndex(index, true);
    });

    // ---------- Click-to-centre + drag ----------
    /*
      Native horizontal scrollers respond to touch, trackpad and wheel on
      their own, but not to a mouse press-and-drag — which is the gesture
      a 3D wheel most invites. Pointer Events cover mouse/touch/pen in one
      set of handlers; touch is filtered out because the browser is
      already scrolling the strip natively for those and handling it twice
      would double the movement.
    */
    var DRAG_THRESHOLD = 6;
    var dragging = false;
    var dragMoved = false;
    var dragStartX = 0;
    var dragStartScroll = 0;
    var pointerId = null;

    scroller.addEventListener("pointerdown", function (e) {
      if (e.pointerType === "touch") return;
      if (e.pointerType === "mouse" && e.button !== 0) return;
      dragging = true;
      dragMoved = false;
      dragStartX = e.clientX;
      dragStartScroll = scroller.scrollLeft;
      pointerId = e.pointerId;
      window.clearTimeout(idleTimer);
      // A user gesture always wins over an animation still in flight.
      cancelAnim();
    });

    scroller.addEventListener("pointermove", function (e) {
      if (!dragging || e.pointerId !== pointerId) return;
      var dx = e.clientX - dragStartX;
      if (!dragMoved && Math.abs(dx) < DRAG_THRESHOLD) return;
      if (!dragMoved) {
        dragMoved = true;
        scroller.setPointerCapture(pointerId);
        scroller.classList.add("wt-new-arrivals-v2__scroller--dragging");
      }
      scroller.scrollLeft = dragStartScroll - dx;
      e.preventDefault();
    });

    function endDrag(e) {
      if (!dragging || (e && e.pointerId !== pointerId)) return;
      dragging = false;
      scroller.classList.remove("wt-new-arrivals-v2__scroller--dragging");
      if (pointerId !== null && scroller.hasPointerCapture(pointerId)) {
        scroller.releasePointerCapture(pointerId);
      }
      pointerId = null;
      settle();
    }

    scroller.addEventListener("pointerup", endDrag);
    scroller.addEventListener("pointercancel", endDrag);

    /* A plain click (no drag) over an off-centre card centres it.
       Registered in the CAPTURE phase on the scroller — i.e. before the
       click reaches whatever was actually clicked — for two reasons:
       preventDefault() here still cancels a link's navigation, which is
       what stops a click-drag that happens to start on a product image
       from also opening that product; and off-centre cards are inert
       (pointer-events: none), so their clicks land on the scroller and
       have to be resolved by hit-testing the card boxes instead of by a
       listener on the cards themselves. */
    scroller.addEventListener(
      "click",
      function (e) {
        if (dragMoved) {
          dragMoved = false;
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        /* Clicks on a card's own controls are that control's business.
           Without this the centring pass below still runs for them, and
           while it is a no-op on an already-centred card, there is no
           reason for a press of Add to cart to consult the wheel at all. */
        if (e.target && e.target.closest && e.target.closest("[data-card-actions]")) {
          return;
        }
        var best = null;
        var bestDistance = Infinity;
        slides.forEach(function (slide, i) {
          if (slide.getAttribute("aria-hidden") === "true") return;
          var rect = slide.getBoundingClientRect();
          if (e.clientY < rect.top || e.clientY > rect.bottom) return;
          if (e.clientX < rect.left || e.clientX > rect.right) return;
          var distance = Math.abs(e.clientX - (rect.left + rect.width / 2));
          if (distance < bestDistance) {
            bestDistance = distance;
            best = i;
          }
        });
        if (best !== null) {
          var bestIndex = nearestIndexFor(best);
          if (bestIndex !== Math.round(progress())) scrollToIndex(bestIndex, true);
        }
      },
      true
    );

    // ---------- Wheel ----------
    /*
      One rule: vertical wheel input always belongs to the page, never to
      this carousel. Only horizontal intent turns the wheel.

      This replaces an arrangement where homepage-scroll-snap.js poured
      vertical wheel deltas into this strip and refused to advance the page
      until the last product had been reached — so getting to the sections
      below the fold meant scrolling through the entire catalogue. Any
      "consume vertical scroll until exhausted" rule has that failure mode;
      the only version that can never trap someone is not consuming vertical
      scroll at all. It is also what Apple and Dyson do with their own
      product carousels, so it is what visitors already expect.

      The strip is a real overflow-x scroller, so the browser would handle
      horizontal wheel by itself — except Lenis calls preventDefault() on
      every wheel event it sees, which would swallow deltaX too. Claiming
      horizontal-intent events here with stopPropagation() keeps them from
      ever reaching Lenis's window-level listener, while vertical events are
      left untouched and bubble on to it for a smooth page scroll. That is
      also why the section markup carries data-lenis-prevent-TOUCH rather
      than the both-axes data-lenis-prevent: the wheel axis has to stay
      negotiable.

      Trackpads report a diagonal component on almost every gesture, so
      intent is decided by which axis dominates rather than by deltaX being
      non-zero at all.
    */
    scroller.addEventListener(
      "wheel",
      function (e) {
        if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;

        var atStart = scroller.scrollLeft <= 0;
        var atEnd = scroller.scrollLeft >= maxScroll() - 1;
        // Let a horizontal flick past either end chain out to the browser's
        // back/forward gesture exactly as a normal scroller would, instead
        // of silently eating it. (overscroll-behavior-x: contain in the CSS
        // still stops the page itself from rubber-banding.)
        if ((atStart && e.deltaX < 0) || (atEnd && e.deltaX > 0)) return;

        e.preventDefault();
        e.stopPropagation();

        cancelAnim();
        window.clearTimeout(idleTimer);
        scroller.scrollLeft += e.deltaX;
        idleTimer = window.setTimeout(settle, SCROLL_IDLE_MS);
      },
      { passive: false }
    );

    // ---------- Add to cart ----------
    // Unchanged in behaviour from the previous version of this section:
    // the same /cart/add.js call assets/product-form.js makes (fetchConfig
    // for the Accept header, Content-Type stripped so FormData sets its
    // own multipart boundary, sections/sections_url so cart-drawer can
    // refresh). Only its location moved — from a flip-card back face to
    // the front of the card.
    var cartDrawerEl = document.querySelector("cart-drawer");
    var cartAddUrl =
      (window.routes && window.routes.cart_add_url) || "/cart/add.js";

    function setLoading(btn, isLoading) {
      var spinner = btn.querySelector(".loading-overlay__spinner");
      btn.classList.toggle("loading", isLoading);
      if (spinner) spinner.classList.toggle("hidden", !isLoading);
    }

    function resetButton(btn, label) {
      btn.disabled = false;
      btn.removeAttribute("aria-disabled");
      btn.classList.remove("wt-new-arrivals-v2__add--error");
      setLoading(btn, false);
      // The server-rendered label is already in the visitor's language.
      if (label) label.textContent = label.getAttribute("data-label-default") || "Add to cart";
    }

    function addToCart(slide) {
      var btn = slide.querySelector("[data-card-add]");
      var label = slide.querySelector("[data-card-add-label]");
      var errorEl = slide.querySelector("[data-card-error]");
      if (!btn || btn.disabled) return;

      var variantId = btn.getAttribute("data-variant-id");
      if (!variantId) return;

      btn.disabled = true;
      btn.setAttribute("aria-disabled", "true");
      btn.classList.remove("wt-new-arrivals-v2__add--error");
      setLoading(btn, true);
      if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = "";
      }

      var config =
        typeof fetchConfig === "function"
          ? fetchConfig("javascript")
          : {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/javascript"
              }
            };
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      delete config.headers["Content-Type"];

      var formData = new FormData();
      formData.append("id", variantId);
      formData.append("quantity", 1);
      if (cartDrawerEl && typeof cartDrawerEl.getSectionsToRender === "function") {
        formData.append(
          "sections",
          cartDrawerEl.getSectionsToRender().map(function (s) {
            return s.id;
          })
        );
      }
      formData.append("sections_url", window.location.pathname);
      config.body = formData;

      function fail(message) {
        setLoading(btn, false);
        btn.classList.add("wt-new-arrivals-v2__add--error");
        if (label) label.textContent = "Error";
        if (errorEl) {
          errorEl.textContent = message;
          errorEl.hidden = false;
        }
        window.setTimeout(function () {
          resetButton(btn, label);
        }, 2200);
      }

      fetch(cartAddUrl, config)
        .then(function (response) {
          return response.json();
        })
        .then(function (response) {
          if (response.status) {
            if (
              typeof publish === "function" &&
              typeof PUB_SUB_EVENTS !== "undefined" &&
              PUB_SUB_EVENTS.cartError
            ) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "new-arrivals-v2",
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message
              });
            }
            fail(
              response.description ||
                response.message ||
                "Something went wrong. Please try again."
            );
            return;
          }

          if (cartDrawerEl && typeof cartDrawerEl.renderContents === "function") {
            cartDrawerEl.renderContents(response, false);
          }
          if (
            typeof publish === "function" &&
            typeof PUB_SUB_EVENTS !== "undefined" &&
            PUB_SUB_EVENTS.cartUpdate
          ) {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: "new-arrivals-v2",
              productVariantId: variantId,
              cartData: response
            });
          }

          setLoading(btn, false);
          if (label) label.textContent = "Added";
          window.setTimeout(function () {
            resetButton(btn, label);
          }, 1500);
        })
        .catch(function () {
          fail("Something went wrong. Please try again.");
        });
    }

    /*
      On a touch screen the button activates from a captured pointer, not
      from the click that would otherwise follow.

      A click is delivered by hit-testing the finger's coordinates against
      the layout at the moment it is dispatched. On a wheel that is still
      turning, the button is not where it was when the finger landed — a
      card mid-turn is both offset and rescaled, and a 34px circle can be
      most of its own width away by then. The click lands on the photo, or
      on nothing, and the press is silently lost. That is the whole of the
      original bug, and it is why the wheel used to have to be held still
      for the button to be usable at all.

      setPointerCapture retargets every later event for that pointer to
      this button regardless of what is underneath it. The press belongs to
      the button from first contact, so the wheel is free to keep turning
      through it — which is what lets the price row stay exempt from the
      autoplay clock entirely (see onInteraction) instead of having to
      freeze it.

      A finger that travels past TAP_SLOP was a swipe: the browser claims
      the gesture, fires pointercancel, and the press is abandoned here
      while the scroller's touchmove handler takes it as a wheel turn.
    */
    slides.forEach(function (slide) {
      var btn = slide.querySelector("[data-card-add]");
      if (!btn) return;

      var tapPointerId = null;
      var tapDownX = 0;
      var tapDownY = 0;

      btn.addEventListener("pointerdown", function (e) {
        // Mouse and pen keep the plain click path: there is no moving
        // target for them, because the wheel pauses under a cursor.
        if (!COARSE_POINTER.matches || e.pointerType === "mouse") return;
        tapPointerId = e.pointerId;
        tapDownX = e.clientX;
        tapDownY = e.clientY;
        if (btn.setPointerCapture) {
          try {
            btn.setPointerCapture(e.pointerId);
          } catch (err) {
            /* Capture is best-effort: without it the click path below still
               runs, which is exactly the behaviour mouse and pen get. */
          }
        }
      });

      btn.addEventListener("pointerup", function (e) {
        if (tapPointerId === null || e.pointerId !== tapPointerId) return;
        tapPointerId = null;
        if (
          Math.abs(e.clientX - tapDownX) > TAP_SLOP ||
          Math.abs(e.clientY - tapDownY) > TAP_SLOP
        ) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        addToCart(slide);
      });

      function abandonTap() {
        tapPointerId = null;
      }
      btn.addEventListener("pointercancel", abandonTap);
      btn.addEventListener("lostpointercapture", abandonTap);

      /* Still bound, and still the only path for mouse, pen, keyboard and
         assistive technology. A compatibility click following a pointerup
         that already added the item is harmless: addToCart() disables the
         button synchronously and returns early while it is disabled, so the
         second call cannot reach the cart. */
      btn.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        addToCart(slide);
      });
    });

    // ---------- Colour swatches ----------
    /*
      A product that comes in several colours prints one dot per colour
      (snippets/product-colour-siblings.liquid, mode: 'card'). In this store
      every colour is a product of its own, so a press does not pick a
      variant: it turns the card into the sibling product. Everything the
      card shows or acts on is swapped from the data the dot carries — the
      two product links, the name, the photo, the price and the variant the
      Add to cart button sends.

      Delegated on the root rather than bound per dot: the scroller's
      capture-phase click handler already lets anything inside
      [data-card-actions] through untouched, so the click arrives here by
      ordinary bubbling. Off-centre cards are pointer-events: none, so only
      the centred card's dots can ever be pressed.
    */
    function swatchFrom(e) {
      var swatch = e.target && e.target.closest && e.target.closest("[data-card-swatch]");
      return swatch && root.contains(swatch) ? swatch : null;
    }

    // Debug readout plumbing (see the ?vdebug block below). Inert unless on.
    var vdbgBox = null;
    var vdbgHead = "";
    var vdbgLines = [];
    function vdesc(el, swatchOnly) {
      if (!el || !el.tagName) return swatchOnly ? "" : "?";
      var sw = el.closest ? el.closest("[data-card-swatch]") : null;
      if (swatchOnly) return sw ? "SWATCH " + (sw.getAttribute("title") || "") : "";
      return el.tagName.toLowerCase() + "." + String(el.className && el.className.baseVal === undefined ? el.className : "").split(" ")[0].replace("wt-new-arrivals-v2__", "");
    }
    function vlog(msg) {
      if (!vdbgBox) return;
      var line = (performance.now() / 1000).toFixed(2) + " " + msg;
      if (window.console) console.log("[vdebug] " + line);
      vdbgLines.push(line);
      vdbgLines = vdbgLines.slice(-22);
      vdbgBox.textContent = vdbgHead + "\n" + vdbgLines.join("\n");
    }

    function applySwatch(swatch) {
      // Someone choosing a colour or size is studying this card: autoplay
      // stands still on it until they move the wheel (see swatchHold).
      swatchHold = true;

      var slide = swatch.closest("[data-carousel-card]");
      if (!slide || swatch.classList.contains("is-current")) {
        vlog("  apply SKIP already current: " + swatch.getAttribute("title"));
        return;
      }

      var btn = slide.querySelector("[data-card-add]");
      // Mid-request the button belongs to addToCart(); changing the variant
      // under it would report the wrong product. The press is simply ignored.
      if (btn && btn.classList.contains("loading")) {
        vlog("  apply SKIP cart button busy");
        return;
      }
      vlog("  APPLY " + swatch.getAttribute("title"));

      var url = swatch.getAttribute("data-url");
      var title = swatch.getAttribute("data-title");
      var available = swatch.getAttribute("data-available") === "true";

      slide.querySelectorAll("[data-card-link]").forEach(function (link) {
        if (url) link.setAttribute("href", url);
      });
      var name = slide.querySelector(".wt-new-arrivals-v2__card-name");
      if (name && title) name.textContent = title;

      var img = slide.querySelector("img.wt-new-arrivals-v2__img");
      var src = swatch.getAttribute("data-image");
      if (img && src) {
        // srcset first: with it still pointing at the old colour the browser
        // would keep choosing from the old candidates.
        img.setAttribute("srcset", swatch.getAttribute("data-srcset") || "");
        img.setAttribute("src", src);
        img.setAttribute("alt", swatch.getAttribute("data-alt") || title || "");
      }

      var price = slide.querySelector(".wt-new-arrivals-v2__card-price");
      var priceTpl = swatch.querySelector("[data-swatch-price]");
      if (price && priceTpl) price.innerHTML = priceTpl.innerHTML;

      if (btn) {
        var label = slide.querySelector("[data-card-add-label]");
        var errorEl = slide.querySelector("[data-card-error]");
        btn.setAttribute("data-variant-id", swatch.getAttribute("data-variant-id") || "");
        btn.classList.remove("wt-new-arrivals-v2__add--error");
        btn.disabled = !available;
        if (available) {
          btn.removeAttribute("aria-disabled");
        } else {
          btn.setAttribute("aria-disabled", "true");
        }
        if (label) {
          label.textContent = available
            ? label.getAttribute("data-label-default") || "Add to cart"
            : label.getAttribute("data-label-soldout") || "Sold out";
        }
        if (errorEl) {
          errorEl.hidden = true;
          errorEl.textContent = "";
        }
      }

      slide.querySelectorAll("[data-card-swatch]").forEach(function (other) {
        var on = other === swatch;
        other.classList.toggle("is-current", on);
        other.setAttribute("aria-pressed", on ? "true" : "false");
      });
    }

    /* Mouse, pen, keyboard and assistive technology. Also runs as the
       compatibility click after a touch press that already swapped the
       card, where applySwatch() returns early on is-current. */
    root.addEventListener("click", function (e) {
      var swatch = swatchFrom(e);
      if (!swatch) return;
      e.preventDefault();
      applySwatch(swatch);
    });

    /*
      On a touch screen a swatch switches the card the instant a finger
      lands on it, not when the finger lifts.

      Two release-time versions were tried first — the plain click, then a
      captured pointerup — and both still failed on a Galaxy A52 while
      working on other phones. Everything that happens after contact is
      the browser's to take away: a few pixels of finger roll on a strip
      that pans both ways is claimed as a scroll (pointercancel, and no
      click either), and the click itself is re-aimed by Android's touch
      adjustment. Contact is the one moment nothing has been decided yet.

      Bound for both pointerdown and touchstart, in the capture phase, so no
      other handler and no browser lacking one of the two can stand in the
      way; the second to arrive finds the swatch already current and
      returns.

      The picker is a band across the whole card now, so a swipe of the
      wheel will often begin on it. What undoes such a press is the wheel
      actually turning to another card (render() calls undoPress() when a
      different card is centred) — never how far the finger travelled. An
      earlier version put the card back whenever the finger moved 10px or
      the browser cancelled the pointer, and on a real phone a thumb
      stretched across to the far side of the card rolls further than that
      on an ordinary tap: the far blocks flashed and went straight back.
      The undo stays armed for a moment after the finger lifts, because a
      flick carries the wheel on after release.
    */
    var pressUndo = null;
    var pressUndoTimer = null;

    function pressSwatch(e) {
      if (e.pointerType === "mouse") return;
      var swatch = swatchFrom(e);
      if (!swatch || swatch.classList.contains("is-current")) return;
      var slide = swatch.closest("[data-carousel-card]");
      window.clearTimeout(pressUndoTimer);
      pressUndo = slide ? slide.querySelector("[data-card-swatch].is-current") : null;
      applySwatch(swatch);
    }

    function undoPress() {
      var previous = pressUndo;
      pressUndo = null;
      window.clearTimeout(pressUndoTimer);
      if (previous) {
        vlog("  UNDO (wheel turned) back to " + previous.getAttribute("title"));
        applySwatch(previous);
      }
    }

    function endPress() {
      if (!pressUndo) return;
      window.clearTimeout(pressUndoTimer);
      pressUndoTimer = window.setTimeout(function () {
        pressUndo = null;
      }, 700);
    }

    var PRESS_OPTS = { capture: true, passive: true };
    root.addEventListener("pointerdown", pressSwatch, PRESS_OPTS);
    root.addEventListener("touchstart", pressSwatch, PRESS_OPTS);
    root.addEventListener("pointerup", endPress, PRESS_OPTS);
    root.addEventListener("touchend", endPress, PRESS_OPTS);

    /* TEMPORARY — remove once the Galaxy A52 report is closed. Opening the
       page with ?vdebug in the address logs every touch on this section,
       and what the picker did with it, to the console (prefix [vdebug])
       and to a box on screen, so a phone that cannot switch a swatch can
       show why. vlog() does nothing at all without the parameter. */
    if (/[?&]vdebug\b/.test(window.location.search)) {
      vdbgBox = document.createElement("pre");
      vdbgBox.style.cssText =
        "position:fixed;left:0;top:0;z-index:2147483647;margin:0;padding:4px 6px;" +
        "width:100vw;box-sizing:border-box;background:rgba(0,0,0,.85);color:#0f0;" +
        "font:10px/1.3 monospace;pointer-events:none;white-space:pre-wrap;word-break:break-all";
      document.body.appendChild(vdbgBox);
      vdbgHead =
        "vdebug 5 | " + window.innerWidth + "x" + window.innerHeight +
        " dpr" + window.devicePixelRatio + " coarse:" + COARSE_POINTER.matches +
        " maxTouch:" + navigator.maxTouchPoints + "\n" + navigator.userAgent.slice(-70);
      vlog("ready");
      var vdbgMoves = 0;
      ["pointerdown", "touchstart", "pointermove", "touchmove", "pointercancel", "touchcancel", "pointerup", "touchend", "click"].forEach(function (type) {
        document.addEventListener(
          type,
          function (e) {
            // Moves are counted, not listed: one line per gesture instead of fifty.
            if (type === "pointermove" || type === "touchmove") { vdbgMoves++; return; }
            var p = e.touches && e.touches[0] ? e.touches[0] : e.changedTouches && e.changedTouches[0] ? e.changedTouches[0] : e;
            var x = Math.round(p.clientX || 0);
            var y = Math.round(p.clientY || 0);
            var sw = vdesc(e.target, true);
            var top = document.elementFromPoint(x, y);
            var slide = e.target && e.target.closest ? e.target.closest("[data-carousel-card]") : null;
            vlog(
              type + " " + x + "," + y + " on " + vdesc(e.target) + (sw ? " [" + sw + "]" : "") +
                (top !== e.target ? " top:" + vdesc(top) : "") +
                (slide ? " card" + slide.getAttribute("data-index") + (slide.style.pointerEvents === "none" ? " INERT" : "") : "") +
                (vdbgMoves ? " moves:" + vdbgMoves : "") + (e.defaultPrevented ? " PREVENTED" : "")
            );
            if (type !== "pointerdown" && type !== "touchstart") vdbgMoves = 0;
          },
          { capture: true, passive: true }
        );
      });
    }

    // ---------- Autoplay ----------
    /*
      The wheel advances one card on a timer, so the section demonstrates
      itself to a visitor who never touches it. Everything below exists so
      that timer never fights the person:

        - It only ever calls scrollToIndex(), the same entry point the
          arrows use, so autoplay introduces no motion path of its own.
        - It goes quiet while a pointer is over the section, while the tab
          is hidden, while the section is off screen, and for one full
          interval after ANY interaction (pointer, wheel, key, touch,
          focus). Those are recorded in the capture phase on the root, so
          none of the existing handlers need autoplay awareness — including
          the Add to cart button, which stops propagation.
        - It is off entirely under prefers-reduced-motion: a carousel that
          moves by itself is exactly what that setting is asking about.

      Reaching the last card wraps back to the first. This wheel has ends
      (the arrows disable there), and stopping dead would leave an idle
      homepage parked on a disabled control.
    */
    var autoplayMs = parseInt(root.getAttribute("data-autoplay-interval"), 10);
    if (isNaN(autoplayMs) || autoplayMs < 1000) autoplayMs = 3000;

    var autoplayOn = root.getAttribute("data-autoplay") === "true";
    /* Mouse-only on purpose: on a touch device a tap can leave a lingering
       :hover behind it, which would stop the wheel for good. */
    var hoverCapable = window.matchMedia("(hover: hover)").matches;
    var dotsList = root.querySelector("[data-carousel-dots]");
    /* The clock runs on animation frames rather than setInterval so the
       active dot can show it: elapsed time only accumulates while nothing
       is holding the wheel, and the fill freezes exactly when the clock
       does. */
    var autoplayFrame = null;
    var clockElapsed = 0;
    var clockLastFrame = 0;
    var blockedCache = false;
    var blockedCheckedAt = 0;
    /* Set by a press on a colour block or size ring. While it is set the
       clock does not run and the dot fill stays where it was, so the card
       cannot turn away from someone comparing options. It cannot wedge:
       render() drops it the moment a different card is centred, and the
       tick drops it when the section leaves the screen. */
    var swatchHold = false;

    var HOVER_HOLD =
      "[data-carousel-card]:hover, [data-carousel-prev]:hover," +
      "[data-carousel-next]:hover, [data-carousel-dot]:hover";

    /* Nothing about the pause is REMEMBERED.

       Every earlier version of this kept the reasons to be paused in
       variables kept up to date by events -- first a count, then named
       flags -- and every version got wedged off, because that state is
       only ever as good as the events that maintain it, and the events
       do not always come. A pointer can leave without a mouseleave when
       the tab changes underneath it. A page restored from bfcache, or
       opened in a background tab, can miss the visibilitychange that
       would have cleared `hidden`. Any single missed event left the
       wheel stopped for the rest of the page view, which reads to a
       visitor as simply broken.

       So the tick asks the DOM instead, every time, and keeps no state
       at all. Three cheap synchronous reads once every few seconds: the
       browser cannot be out of date about its own :hover, its own
       document.hidden, or where an element sits in the viewport. There
       is nothing left to go stale, and nothing to keep in sync. */
    function hoveringProduct() {
      /* Deliberately NOT the section. On the homepage this section is
         height:100% inside a 100dvh frame, so its root box is the whole
         screen -- pausing on that stopped the wheel the moment the cursor
         entered the section and kept it stopped while the cursor was
         anywhere on screen, the empty surround included.

         Off-centre cards are pointer-events:none (see render), so the
         only card that can match is the centred one -- exactly the
         product someone means when they hover "the product". Arrows and
         dots count too: a control moving out from under an aiming cursor
         is hostile. */
      return hoverCapable && !!root.querySelector(HOVER_HOLD);
    }

    /* An off-screen section should not animate: it wastes frames, and
       someone scrolling back up should find the wheel roughly where they
       left it rather than several products on. Measured directly rather
       than observed, for the same reason as everything else here. */
    function mostlyOffScreen() {
      var box = root.getBoundingClientRect();
      var h = window.innerHeight || document.documentElement.clientHeight;
      var visible = Math.min(box.bottom, h) - Math.max(box.top, 0);
      return box.height > 0 && visible / box.height < 0.25;
    }

    function autoplayBlocked() {
      return (
        document.hidden ||
        hoveringProduct() ||
        mostlyOffScreen()
      );
    }

    function setDotProgress(value) {
      if (dotsList) dotsList.style.setProperty("--dot-progress", value.toFixed(4));
    }

    // Called from render() on every dot change and on any interaction.
    function resetAutoplayClock() {
      clockElapsed = 0;
      if (autoplayFrame !== null) setDotProgress(0);
    }

    function autoplayStep(now) {
      autoplayFrame = window.requestAnimationFrame(autoplayStep);

      /* Capped so a frame gap (tab restored, long task) cannot jump the
         fill forward or fire an advance the visitor never saw coming. */
      var dt = clockLastFrame ? Math.min(now - clockLastFrame, 100) : 0;
      clockLastFrame = now;

      // The DOM reads are cheap but not free; five times a second is
      // plenty to notice a hover or a scroll away.
      if (now - blockedCheckedAt > 200) {
        blockedCache = autoplayBlocked();
        blockedCheckedAt = now;
        // Scrolled away: the comparison is over, so the wheel is free to
        // turn again when the section comes back.
        if (swatchHold && mostlyOffScreen()) swatchHold = false;
      }
      if (swatchHold) return;

      /* Held: the fill freezes where it is.

         `tapHold` is deliberately NOT in here. A finger on the price row
         holds nothing — the wheel turns through the press exactly as if
         the card had not been touched, which is the whole point of the
         exemption. The button survives that because it activates from a
         captured pointer rather than from a hit test at click time. */
      if (blockedCache || dragging || touching) return;
      // A settle or a click-to-centre still in flight owns the strip.
      if (animFrame !== null) return;

      clockElapsed += dt;
      if (clockElapsed < autoplayMs) {
        setDotProgress(clockElapsed / autoplayMs);
        return;
      }

      /* No wrap-to-zero: travel is unbounded, so "next" is always simply
         one more. That is what makes an unattended wheel turn steadily in
         one direction instead of sweeping back across the whole rail every
         time it reaches the last product. */
      setDotProgress(1);
      clockElapsed = 0;
      var next = Math.round(progress()) + 1;
      scrollToIndex(LOOP ? next : next > count - 1 ? 0 : next, true);
    }

    function startAutoplay() {
      if (!autoplayOn || autoplayFrame !== null) return;
      if (count < 2 || REDUCED_MOTION.matches) return;
      clockElapsed = 0;
      clockLastFrame = 0;
      blockedCheckedAt = 0;
      setDotProgress(0);
      autoplayFrame = window.requestAnimationFrame(autoplayStep);
    }

    function stopAutoplay() {
      if (autoplayFrame === null) return;
      window.cancelAnimationFrame(autoplayFrame);
      autoplayFrame = null;
      // No clock, no countdown: the active pill goes back to solid gold.
      if (dotsList) dotsList.style.removeProperty("--dot-progress");
    }

    if (autoplayOn) {
      /* "click" is in here as well as "pointerdown" because an activation
         that never went through a pointer still counts as the visitor
         acting: assistive technology firing a button, and any programmatic
         .click(), both raise click alone. Without it autoplay would keep
         advancing underneath someone driving the arrows that way. */
      /* The price row is exempt on touch screens. Pressing Add to cart is
         not a request to do anything to the wheel — not to turn it, not to
         hold it, not to restart its countdown — so it reaches none of this.
         The wheel keeps its own time straight through the press.

         That is safe to do here only because the button no longer depends
         on the wheel standing still: it activates from a captured pointer
         (see the Add to cart section), so the press belongs to the button
         from first contact and a card turning underneath it changes
         nothing. Without that capture this exemption would hand back the
         intermittent miss it was written to remove. */
      function onInteraction(e) {
        if (inTapZone(e)) return;
        // A swatch press freezes the countdown (swatchHold) rather than
        // restarting it, so the dot fill must not jump back to empty.
        if (e.target && e.target.closest && e.target.closest("[data-card-swatch]")) return;
        resetAutoplayClock();
      }

      ["pointerdown", "touchstart", "wheel", "keydown", "click"].forEach(function (type) {
        root.addEventListener(
          type,
          onInteraction,
          { capture: true, passive: true }
        );
      });
      root.addEventListener("focusin", onInteraction, true);

      // Safari <14 only has the deprecated addListener().
      var onMotionPrefChange = function () {
        if (REDUCED_MOTION.matches) stopAutoplay();
        else startAutoplay();
      };
      if (REDUCED_MOTION.addEventListener) {
        REDUCED_MOTION.addEventListener("change", onMotionPrefChange);
      } else if (REDUCED_MOTION.addListener) {
        REDUCED_MOTION.addListener(onMotionPrefChange);
      }

      startAutoplay();
    }

    // ---------- Lifecycle ----------

    function relayout(keepIndex) {
      if (!measure()) return;
      layoutRail();
      scrollToIndex(typeof keepIndex === "number" ? keepIndex : activeIndex, false);
      render();
    }

    var resizeTimer = null;
    function onResize() {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        relayout(activeIndex);
      }, 120);
    }
    window.addEventListener("resize", onResize);

    /* The stage's own height is driven by whatever space the section has
       left over, which on the homepage is set by a JS-sized snap frame
       rather than by the viewport alone — so a window resize listener is
       not enough on its own to catch every size change that alters card
       width. */
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(onResize);
      ro.observe(stage);
    }

    /* Card width comes from the stage height via aspect-ratio, and images
       are lazily loaded — so the first measure() can land before layout
       has settled. Re-run once everything has loaded. */
    window.addEventListener("load", function () {
      relayout(activeIndex);
    });

    relayout(homeBase);
  }

  function initAll(scope) {
    (scope || document)
      .querySelectorAll("[data-new-arrivals-v2]")
      .forEach(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initAll();
    });
  } else {
    initAll();
  }

  /* The theme editor swaps in fresh section markup on every settings
     change, after DOMContentLoaded has already fired. The new node has no
     data-new-arrivals-v2-init flag on it, so init() runs for it and skips
     any section that is already wired. */
  document.addEventListener("shopify:section:load", function (e) {
    initAll(e.target);
  });
})();
